import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Header } from '@components/Header';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { JobStatusText } from '@components/JobStatusText';
import { downloadCSV } from '@helpers/downloadCSV';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { secondsToHHMMSS } from '@helpers/secondsToHHMMSS';
import { toUnixSecondsQueryParam } from '@helpers/toUnixSecondsQueryParam';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ReportsService } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { TJobsFilterUpdate, useJobsFilterQueryParams } from 'app/hooks/useJobsFilterQueryParams';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { reportsKeys, TUserJobsReportListParams } from 'app/queries/reports/reportsKeys';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { buildUserJobsReportRequest } from './api/buildUserJobsReportRequest';
import { createFetchUserJobsReportQueryFn } from './api/fetchUserJobsReport';
import { buildUserJobsReportCsvRows, USER_JOBS_REPORT_CSV_HEADERS } from './helpers/buildUserJobsReportCsvRows';
import { CellContent } from './styles/CellContent';
import { Content } from './styles/Content';

export const UserJobsReport = () => {
  const { call } = useAPI();
  const { openModal } = useModalContext();
  const { searchParams, page, pageSize, setPage, setPageSize, updateParams } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });
  const { jobsFilter, applyJobsFilter } = useJobsFilterQueryParams({ searchParams, updateParams });
  const { userId = '' } = useParams<{ userId: string }>();
  const { workspace, organization } = useAuthContext();
  const workspaceId = workspace?.id ?? '';
  const jobsFrom = toUnixSecondsQueryParam(jobsFilter.from);
  const jobsTo = toUnixSecondsQueryParam(jobsFilter.to);
  const listParams = useMemo<TUserJobsReportListParams>(
    () => ({ page, pageSize, jobsFrom, jobsTo }),
    [jobsFrom, jobsTo, page, pageSize],
  );

  const reportsQuery = useQuery({
    queryKey: reportsKeys.userJobsList(workspaceId, userId, listParams),
    queryFn: createFetchUserJobsReportQueryFn(call),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[0].workspaceId === workspaceId && previousQuery.queryKey[0].userId === userId
        ? previousData
        : undefined,
    enabled: workspaceId !== '' && userId !== '',
  });
  const jobs = reportsQuery.data?.data ?? [];
  const jobsCount = reportsQuery.data?.count ?? 0;

  const exportMutation = useMutation({
    mutationFn: () =>
      call(() =>
        ReportsService.getUserJobsReport(
          buildUserJobsReportRequest(workspaceId, userId, { ...listParams, page: 1, pageSize: Math.max(jobsCount, 1) }),
        ),
      ),
    onSuccess: (response) => {
      downloadCSV(
        buildUserJobsReportCsvRows(response.data, {
          organizationName: organization?.name ?? '',
          workspaceName: workspace?.name ?? '',
        }),
        USER_JOBS_REPORT_CSV_HEADERS,
      );
    },
    onError: () => toast.error('Failed to export the user jobs report'),
  });

  const openFiltersModal = () => {
    openModal(ModalNamesEnum.JobsReportFilters, {
      jobsFilter,
      jobsFilterOnChange: (update: TJobsFilterUpdate) =>
        applyJobsFilter(update, { onUnchanged: () => void reportsQuery.refetch() }),
      onSubmit: () => undefined,
    });
  };

  const isLoading = reportsQuery.isFetching || exportMutation.isPending;
  const isExportDisabled = reportsQuery.isPlaceholderData || !reportsQuery.isSuccess || exportMutation.isPending;

  return (
    <>
      <Helmet>
        <title>User Jobs Report - AutoScript</title>
      </Helmet>
      <Header
        title="User Jobs Report"
        buttonLabel={jobs.length ? 'Export CSV' : undefined}
        buttonOnClick={jobs.length ? () => exportMutation.mutate() : undefined}
        buttonDisabled={isExportDisabled}
        buttonLoading={exportMutation.isPending}
        onFilterClick={openFiltersModal}
        filterCounter={jobsFilter.from || jobsFilter.to ? 1 : 0}
      />

      <LinearProgressLoader active={isLoading} />
      <Content>
        {reportsQuery.isError ? (
          <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
            <span>Failed to load the user jobs report.</span>
            <TextButton type="button" onClick={() => void reportsQuery.refetch()}>
              Retry
            </TextButton>
          </div>
        ) : null}
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th style={{ width: '20%' }}>Member</Th>
                <Th style={{ width: '20%' }}>Job Title</Th>
                <Th style={{ width: '20%' }}>Status</Th>
                <Th style={{ width: '20%' }}>Created</Th>
                <Th style={{ width: '20%' }}>Duration</Th>
              </Tr>
            </Thead>
            <Tbody>
              {jobs.map((data) => (
                <Tr key={data.id}>
                  <Td>
                    <CellContent>
                      <span style={{ display: 'block' }}>
                        {data.name} {data.lastname}
                      </span>
                      <span style={{ display: 'block' }}>{data.email}</span>
                    </CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.jobName || 'Unnamed'}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>
                      <JobStatusText status={data.status} removed={data.removed} />
                    </CellContent>
                  </Td>
                  <Td>
                    <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{secondsToHHMMSS(data.duration ?? 0)}</CellContent>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
        <PagePagination
          totalItemsCount={jobsCount}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Content>
    </>
  );
};
